// src/pages/league-dashboard/live-draft/components/DraftCompleteSummary.js
// Post-draft summary: completion banner, CSV export, trade history, team values
import React, { useMemo, useState } from 'react';
import { Trophy, Download, ArrowLeftRight, BarChart3, ChevronDown, ChevronUp } from 'lucide-react';
import { dynastyTheme } from '../../../../services/colorService';
import { downloadDraftCSV } from '../utils/draftHelpers';

export const DraftCompleteSummary = ({ picks = [], completedTrades = [], leagueName, year, allTeams = [] }) => {
  const [showTrades, setShowTrades] = useState(true);
  const [showValues, setShowValues] = useState(true);

  // Compute per-team values from picks
  const teamValues = useMemo(() => {
    const values = new Map();
    for (const pick of picks) {
      if (!pick.league_player_id || !pick.current_owner_team_id) continue;
      const teamId = pick.current_owner_team_id;
      const salary = parseFloat(pick.salary) || 0;
      const current = values.get(teamId) || {
        teamId,
        teamName: pick.current_owner?.team_name || 'Unknown',
        totalSalary: 0,
        playerCount: 0
      };
      current.totalSalary += salary;
      current.playerCount += 1;
      values.set(teamId, current);
    }
    return Array.from(values.values()).sort((a, b) => b.totalSalary - a.totalSalary);
  }, [picks]);

  // Get pick display for trade
  const getPickDisplay = (pickId) => {
    const pick = picks.find(p => p.pick_id === pickId);
    if (!pick) return `Pick ${pickId}`;
    return `#${pick.overall_pick_number} (Rd ${pick.round})`;
  };

  return (
    <div className="space-y-4">
      {/* Completion Banner */}
      <div className={`${dynastyTheme.components.card.highlighted} p-6 border-2 border-yellow-500/30`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Trophy className={`w-10 h-10 ${dynastyTheme.classes.text.primary}`} />
            <div>
              <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
                Draft Complete!
              </h2>
              <p className={`${dynastyTheme.classes.text.neutralLight}`}>
                {picks.filter(p => p.league_player_id).length} players drafted
                {picks.filter(p => !p.league_player_id).length > 0 &&
                  ` | ${picks.filter(p => !p.league_player_id).length} skipped`}
              </p>
            </div>
          </div>
          <button
            onClick={() => downloadDraftCSV(picks, leagueName, year)}
            className={`${dynastyTheme.utils.getComponent('button', 'primary', 'md')} flex items-center gap-2`}
          >
            <Download className="w-4 h-4" />
            Download CSV
          </button>
        </div>
      </div>

      {/* Team Values Summary */}
      {teamValues.length > 0 && (
        <div className={`${dynastyTheme.components.card.base} p-4`}>
          <button
            onClick={() => setShowValues(!showValues)}
            className={`flex items-center justify-between w-full`}
          >
            <div className="flex items-center gap-2">
              <BarChart3 className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
              <h3 className={`text-base font-bold ${dynastyTheme.classes.text.white}`}>
                Team Draft Values
              </h3>
            </div>
            {showValues ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
          </button>
          {showValues && (
            <div className="mt-3 space-y-1">
              {teamValues.map((team, idx) => (
                <div
                  key={team.teamId}
                  className={`flex items-center justify-between p-2 rounded ${idx % 2 === 0 ? 'bg-neutral-800/50' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-6 text-center text-sm font-mono ${dynastyTheme.classes.text.primary}`}>
                      {idx + 1}
                    </span>
                    <span className={dynastyTheme.classes.text.white}>{team.teamName}</span>
                    <span className={`text-xs ${dynastyTheme.classes.text.neutralDark}`}>
                      ({team.playerCount} picks)
                    </span>
                  </div>
                  <span className={`font-mono font-bold ${dynastyTheme.classes.text.primary}`}>
                    ${team.totalSalary.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Trades During Draft */}
      {completedTrades.length > 0 && (
        <div className={`${dynastyTheme.components.card.base} p-4`}>
          <button
            onClick={() => setShowTrades(!showTrades)}
            className={`flex items-center justify-between w-full`}
          >
            <div className="flex items-center gap-2">
              <ArrowLeftRight className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
              <h3 className={`text-base font-bold ${dynastyTheme.classes.text.white}`}>
                Trades During Draft ({completedTrades.length})
              </h3>
            </div>
            {showTrades ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
          </button>
          {showTrades && (
            <div className="mt-3 space-y-3">
              {completedTrades.map((trade, idx) => (
                <div
                  key={trade.trade_id || idx}
                  className={`p-3 rounded-lg border ${dynastyTheme.classes.border.neutral} bg-neutral-800/30`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>
                      {trade.proposing_team_name} ↔ {trade.receiving_team_name}
                    </span>
                    {trade.resolved_at && (
                      <span className={`text-xs ${dynastyTheme.classes.text.neutralDark}`}>
                        {new Date(trade.resolved_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className={dynastyTheme.classes.text.neutralDark}>{trade.proposing_team_name} sent: </span>
                      <span className={dynastyTheme.classes.text.neutralLight}>
                        {(trade.offering_picks || []).map(id => getPickDisplay(id)).join(', ') || 'Nothing'}
                      </span>
                    </div>
                    <div>
                      <span className={dynastyTheme.classes.text.neutralDark}>{trade.receiving_team_name} sent: </span>
                      <span className={dynastyTheme.classes.text.neutralLight}>
                        {(trade.requesting_picks || []).map(id => getPickDisplay(id)).join(', ') || 'Nothing'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DraftCompleteSummary;
